import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import CreatableSelect from 'react-select/creatable';
import { MultiValue } from 'react-select';
import { IoArrowBack } from 'react-icons/io5';
import { LoaderCircle, Plus, Trash2 } from 'lucide-react';

import { JobListing } from '../../types/jobs';
import { useThemeStore } from '../../state/theme';
import { BtnDefault, BtnPrimary } from '../common/Buttons';
import { updateJobListing } from '../../hooks/mutations';
import { SelectOption } from '../common/Select';

type JobListingFormState = Omit<
  JobListing,
  'requirements' | 'perks' | 'whatYouBring'
> & {
  requirements: string;
  perks: string;
  whatYouBring: string;
};

const inputClass = (isDark: boolean) =>
  `w-full rounded-md p-2 ${
    isDark
      ? 'bg-gray-700 border-greyFifty text-gray-100 focus:border-indigo-500 focus:ring-indigo-500'
      : 'border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
  }`;

const labelClass = (isDark: boolean) =>
  `font-medium text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`;

const TextInput = ({ label, name, register, isDark, ...rest }: any) => (
  <div className="space-y-2">
    {label && <label className={labelClass(isDark)}>{label}</label>}
    <input {...register(name)} {...rest} className={inputClass(isDark)} />
  </div>
);

const TextAreaInput = ({ label, name, register, isDark }: any) => (
  <div className="space-y-2">
    <label className={labelClass(isDark)}>{label}</label>
    <textarea
      {...register(name)}
      className={inputClass(isDark)}
      rows={4}
      placeholder={`Enter each ${label.toLowerCase()} on a new line`}
    />
  </div>
);

const CreatableMultiSelect = ({ value, onChange }: any) => (
  <CreatableSelect
    isMulti
    value={(value || []).map((v: string) => ({ label: v, value: v }))}
    onChange={(newVal) =>
      onChange((newVal as MultiValue<SelectOption>).map((item) => item.value))
    }
    className="w-full"
  />
);

const UpdateJobListing: React.FC<{
  currentJobListing?: JobListing;
  onSuccess?: (data: any) => void;
  onError?: (error: Error) => void;
}> = ({ currentJobListing, onSuccess, onError }) => {
  const { isDarkMode } = useThemeStore();
  const navigate = useNavigate();
  const { register, handleSubmit, control, reset } =
    useForm<JobListingFormState>();
  const [metadata, setMetadata] = useState<JobListing['metadata']>([]);

  const { mutate, isPending } = updateJobListing({
    onSuccess,
    onError,
  });

  useEffect(() => {
    if (currentJobListing) {
      const {
        requirements = [],
        perks = [],
        whatYouBring = [],
      } = currentJobListing;
      reset({
        ...currentJobListing,
        requirements: requirements.join('\n'),
        perks: perks.join('\n'),
        whatYouBring: whatYouBring.join('\n'),
      });
      setMetadata(currentJobListing.metadata || []);
    }
  }, [currentJobListing, reset]);

  const handleMetadataChange = (
    index: number,
    update: Partial<JobListing['metadata'][0]>
  ) => {
    setMetadata((prev) =>
      prev?.map((field, i) => (i === index ? { ...field, ...update } : field))
    );
  };

  const handleMetadataRemove = (index: number) => {
    setMetadata((prev) => prev?.filter((_, i) => i !== index));
  };

  const handleAddMetadata = () => {
    setMetadata((prev) => [
      ...prev,
      { label: '', type: 'text', required: false, options: [] },
    ]);
  };

  const onSubmit = (data: JobListingFormState) => {
    const payload: JobListing = {
      ...data,
      requirements: data.requirements
        .split('\n')
        .map((v) => v.trim())
        .filter(Boolean),
      perks: data.perks
        .split('\n')
        .map((v) => v.trim())
        .filter(Boolean),
      whatYouBring: data.whatYouBring
        .split('\n')
        .map((v) => v.trim())
        .filter(Boolean),
      metadata,
    };
    mutate(payload);
  };

  return (
    <div className="space-y-4">
      <BtnDefault
        type="button"
        onClick={() => navigate('/job-listings')}
        className="flex items-center gap-2"
      >
        <IoArrowBack /> Back
      </BtnDefault>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-5 bg-white p-4 rounded-lg shadow-sm border"
      >
        <h1 className="text-2xl font-bold">Update Job Listing</h1>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextInput
            name="_id"
            register={register}
            isDark={isDarkMode}
            disabled
          />
          <TextInput
            name="title"
            register={register}
            isDark={isDarkMode}
            placeholder="Title"
          />
          <TextInput
            name="category"
            register={register}
            isDark={isDarkMode}
            disabled
          />
          <TextInput
            name="openings"
            register={register}
            isDark={isDarkMode}
            type="number"
            placeholder="Openings"
          />
        </div>

        <div className="space-y-2">
          <label className={labelClass(isDarkMode)}>Skills</label>
          <Controller
            name="skills"
            control={control}
            render={({ field }) => <CreatableMultiSelect {...field} />}
          />
        </div>

        <TextAreaInput
          label="Requirements"
          name="requirements"
          register={register}
          isDark={isDarkMode}
        />
        <TextAreaInput
          label="What You Bring"
          name="whatYouBring"
          register={register}
          isDark={isDarkMode}
        />
        <TextAreaInput
          label="Perks"
          name="perks"
          register={register}
          isDark={isDarkMode}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextInput
            label="Test URL"
            name="testUrl"
            register={register}
            isDark={isDarkMode}
            placeholder="Test URL"
          />
          <TextInput
            label="Threshold Score"
            name="thresholdScore"
            register={register}
            isDark={isDarkMode}
            placeholder="Threshold Score"
          />
        </div>

        <div className="space-y-2">
          <label className={labelClass(isDarkMode)}>Keywords</label>
          <Controller
            name="keywords"
            control={control}
            render={({ field }) => (
              <CreatableMultiSelect
                value={field.value}
                onChange={(val: string[]) => field.onChange(val.map((v) => v))}
              />
            )}
          />
        </div>

        <div className="space-y-2">
          <label className={labelClass(isDarkMode)}>Tags</label>
          <Controller
            name="tags"
            control={control}
            render={({ field }) => (
              <CreatableMultiSelect
                value={field.value}
                onChange={(val: string[]) =>
                  field.onChange(
                    val.map((v) => (v.startsWith('#') ? v : `#${v}`))
                  )
                }
              />
            )}
          />
        </div>

        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Additional Metadata</h2>
            <button
              type="button"
              onClick={handleAddMetadata}
              className="text-sm text-blue-600 hover:underline"
            >
              <Plus size={20} />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {metadata?.map((field, idx) => (
              <div
                key={idx}
                className="flex flex-col gap-2 border border-greyFifty p-4 rounded-lg space-y-2 relative bg-white"
              >
                <button
                  type="button"
                  onClick={() => handleMetadataRemove(idx)}
                  className="absolute -top-2 -right-2 text-blackTen hover:text-red-600"
                >
                  <Trash2 size={16} />
                </button>
                <input
                  value={field.label}
                  onChange={(e) =>
                    handleMetadataChange(idx, { label: e.target.value })
                  }
                  placeholder="Label"
                  className={`${inputClass(isDarkMode)} !-fit`}
                />
                <select
                  value={field.type}
                  onChange={(e) =>
                    handleMetadataChange(idx, { type: e.target.value as any })
                  }
                  className={`${inputClass(isDarkMode)} !-fit`}
                >
                  <option value="text">Text</option>
                  <option value="textarea">Textarea</option>
                  <option value="select">Select</option>
                  <option value="multiSelect">MultiSelect</option>
                  <option value="number">Number</option>
                  <option value="date">Date</option>
                </select>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={field.required}
                    onChange={(e) =>
                      handleMetadataChange(idx, { required: e.target.checked })
                    }
                  />
                  Required
                </label>
                {(field.type === 'select' || field.type === 'multiSelect') && (
                  <CreatableSelect
                    isMulti
                    value={(field.options || []).map((opt) => ({
                      label: opt,
                      value: opt,
                    }))}
                    onChange={(newOptions) =>
                      handleMetadataChange(idx, {
                        options: (newOptions as MultiValue<SelectOption>).map(
                          (o) => o.value
                        ),
                      })
                    }
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end pt-6">
          <BtnPrimary disabled={isPending} type="submit">
            {isPending ? (
              <div className="flex items-center gap-2">
                <span className="ml-2">Updating</span>
                <LoaderCircle size={16} className="animate-spin" />
              </div>
            ) : (
              'Update'
            )}
          </BtnPrimary>
        </div>
      </form>
    </div>
  );
};

export default UpdateJobListing;
